import React, { useState, useEffect } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import {
  Accordion,
  Card,
  CardContent,
  Fade,
  Grid,
  Link,
  Typography,
  WarningCircleIcon,
  useNavigate,
  useLocation,
} from '@forgeglobal/design-framework';
import NeedHelp from '@tcs/common/components/atoms/footer/NeedHelp';
import EnterPasswordForm from '@tcs/client-portal/components/forms/EnterPasswordForm';
import useAuthentication from '@tcs/common/hooks/useAuthentication';
import Header from '@tcs/client-portal-common/components/organisms/DialogLayout/Header';
import MainDisclaimer from '@tcs/common/components/atoms/footer/MainDisclaimer';
import { LoginLink } from './LoginLink';
import { LEGAL_LINKS } from '@tcs/common/assets/constants';
import { useStyles } from './styles';
import {
  AccountIsLockedBanner,
  CanNotFindAccountBanner,
  PasswordChangedBanner,
  AccountIsPasswordExpiredBanner,
} from './Banners';
const Login = () => {
  const classes = useStyles();
  const nav = useNavigate();
  const location = useLocation();
  const { authState } = useOktaAuth();
  const { signIn, isUserLocked, isPasswordExpired } = useAuthentication();
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [showPasswordChangedMessage] = useState(
    !!new URLSearchParams(location.search).get('showChangePasswordBanner'),
  );

  /**
   * Safari and possibly other browsers use a cache that causes problems with
   * back button and useEffect, so we add an additional event listener to reload
   */
  useEffect(() => {
    if (authState?.isAuthenticated) {
      nav('/');
    }

    window.addEventListener(
      'pageshow',
      function (evt) {
        if (evt.persisted) {
          setTimeout(function () {
            window.location.reload();
          }, 10);
        }
      },
      false,
    );
    if (showPasswordChangedMessage) {
      //remove the query string after we set the state.
      nav({ search: undefined }, { replace: true });
    }
  }, [authState, nav, showPasswordChangedMessage]);

  if (!authState) return null;

  const { TERMS_OF_USE, PRIVACY_POLICY, PRIVACY_NOTICE } = LEGAL_LINKS;

  const handleSubmit = async (values) => {
    try {
      setShowErrorMessage(false);
      await signIn(values);
    } catch {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual';
      }
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
      setShowErrorMessage(true);
    }
  };

  const showBanner = showErrorMessage || showPasswordChangedMessage;

  const investmentSponsorVerb = (
    <>
      Please submit valuations via one of the following methods:
      <ul>
        <li>
          Submit a statement or spreadsheet reflecting each individual Forge
          Trust investor’s current value or the investments price per share, as
          applicable.
        </li>
        <li>
          Provide Forge Trust instructions to access this information directly
          through an online portal.
        </li>
      </ul>
      <br></br>
      <strong>
        The updated value you provide must be within the last 12 months in order
        to be considered “current” and accepted by Forge Trust.
      </strong>{' '}
      Any documentation or instructions can be submitted via email to{' '}
      <a href="mailto:Valuations@ForgeTrust.com">Valuations@ForgeTrust.com</a>{' '}
      or fax at <span className={classes.phoneNumberText}>650-745-2636</span>. A
      valuation is required at least annually.
    </>
  );

  return (
    <div>
      <Header />
      {showBanner && (
        <Fade in={showBanner} timeout={750}>
          <div>
            {showErrorMessage && isUserLocked && <AccountIsLockedBanner />}
            {showErrorMessage && isPasswordExpired && (
              <AccountIsPasswordExpiredBanner />
            )}
            {showErrorMessage && !isUserLocked && !isPasswordExpired && (
              <CanNotFindAccountBanner />
            )}
            {showPasswordChangedMessage && <PasswordChangedBanner />}
          </div>
        </Fade>
      )}
      <Card className={classes.card}>
        <CardContent className={classes.cardContainer}>
          <Grid container direction="column" spacing={8}>
            <Grid item container justifyContent="center">
              <Typography variant="h4" data-test-id="title">
                Sign In
              </Typography>
            </Grid>
            <Grid item>
              <EnterPasswordForm onSubmit={handleSubmit} />
              <LoginLink
                linkDescription="Are you an investment sponsor?"
                title="Guide for Investment Sponsors"
                bodyContent={investmentSponsorVerb}
              />
            </Grid>
          </Grid>
        </CardContent>
        <Grid container justifyContent="center" className={classes.footer}>
          <NeedHelp />
        </Grid>
      </Card>
      <Grid container justifyContent="center">
        <Typography
          variant="body2"
          className={classes.policy}
          data-test-id="terms-of-use-text">
          Using the website or the information, tools, features, or
          functionality provided on the website and by Forge Trust Co.
          constitutes acceptance of the Forge Trust Co.{' '}
          <Link href={TERMS_OF_USE} target="_new">
            Terms of Use
          </Link>
          {', '}
          <Link href={PRIVACY_POLICY} target="_new">
            Privacy Policy
          </Link>
          {' and '}
          <Link href={PRIVACY_NOTICE} target="_new">
            Privacy Notice
          </Link>
          {'.'}
        </Typography>
      </Grid>
      <Grid container justifyContent="center">
        <MainDisclaimer />
      </Grid>
    </div>
  );
};

export default Login;
